import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { speichereOrdner } from 'modules/wissen/features/wissenSlice';
import { ordnerMitTiefe } from 'modules/wissen/components/wissenHelpers';

const FARBEN = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b'];

/**
 * Modaler Dialog zum Anlegen/Umbenennen eines Ordners: Name, Farbe und
 * uebergeordneter Ordner (mit Zyklusschutz). Speichert ueber den Slice.
 *
 * @param {{ ordner?: object|null, presetParentId?: number|null, alleOrdner: object[], onClose: Function }} props
 */
export default function OrdnerDialog({ ordner = null, presetParentId = null, alleOrdner, onClose }) {
  const dispatch = useDispatch();
  const [name, setName] = useState(ordner?.name || '');
  const [farbe, setFarbe] = useState(ordner?.farbe || FARBEN[3]);
  const [parentId, setParentId] = useState(ordner ? (ordner.parentId ?? '') : (presetParentId ?? ''));

  const verboten = tabuIds(alleOrdner, ordner?.id);
  const elternOptionen = ordnerMitTiefe(alleOrdner).filter((o) => !verboten.has(o.id));

  const speichern = async () => {
    if (!name.trim()) return;
    await dispatch(
      speichereOrdner({
        uuid: ordner?.uuid,
        name: name.trim(),
        farbe,
        parentId: parentId === '' ? null : Number(parentId),
        sortierung: ordner?.sortierung ?? 0,
      })
    );
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-paper p-6 shadow-xl">
        <h2 className="mb-4 font-serif text-xl font-semibold text-ink">
          {ordner ? 'Ordner umbenennen' : 'Neuer Ordner'}
        </h2>

        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-muted">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && speichern()}
          placeholder="Ordnername"
          autoFocus
          className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink focus:border-brand-600 focus:outline-none"
        />

        <label className="mb-1 mt-4 block text-xs font-semibold uppercase tracking-wide text-ink-muted">Übergeordneter Ordner</label>
        <select
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
          className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-sm text-ink focus:border-brand-600 focus:outline-none"
        >
          <option value="">— Oberste Ebene —</option>
          {elternOptionen.map((o) => (
            <option key={o.uuid} value={o.id}>{' '.repeat(o.tiefe * 2)}{o.name}</option>
          ))}
        </select>

        <label className="mb-1 mt-4 block text-xs font-semibold uppercase tracking-wide text-ink-muted">Farbe</label>
        <div className="flex flex-wrap gap-2">
          {FARBEN.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFarbe(f)}
              className={`h-6 w-6 rounded-full transition-transform hover:scale-110 ${farbe === f ? 'ring-2 ring-ink ring-offset-2' : ''}`}
              style={{ backgroundColor: f }}
            />
          ))}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-ink-muted hover:text-ink">Abbrechen</button>
          <button type="button" onClick={speichern} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500">Speichern</button>
        </div>
      </div>
    </div>
  );
}

/** Ids, die als Elternordner ausscheiden: der Ordner selbst und alle Nachfahren. */
function tabuIds(ordner, ordnerId) {
  const tabu = new Set();
  if (ordnerId == null) return tabu;
  tabu.add(ordnerId);
  let gewachsen = true;
  while (gewachsen) {
    gewachsen = false;
    for (const o of ordner) {
      if (o.parentId != null && tabu.has(o.parentId) && !tabu.has(o.id)) {
        tabu.add(o.id);
        gewachsen = true;
      }
    }
  }
  return tabu;
}
